import { Form, FormInstance } from 'antd';
import { Button, Col, Colors, IcCashierSave, IcPlus, IcShow, IcTrash, Row, Select, Text } from 'basic-ui-lib';
import styled from 'styled-components';
import { FormValues } from '../PlaylistForm';
import DragDropPlaylist from './DragDropPlaylist';
import BaseInput from 'basic-ui-lib/components/Input/BaseInput';
import { useDroppable } from '@dnd-kit/core';
import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { setPageLoading } from 'widgets/PageLoading';
import playlistApis from 'modules/playlist/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import useGetAllStores from 'modules/dashboard/services/hooks/useGetAllStore';
import { ContentModal, useContentModalRef } from 'modules/dashboard/screens/Detail/components';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import { isMp4Url } from 'utils/getYoutubeId';
import getVideoDuration from 'utils/getVideoDuration';
import { IContentItem } from 'modules/playlist/interfaces/api';
import { useNavigate } from 'react-router-dom';

interface ScreenPlaylistProps {
  form?: FormInstance<FormValues>;
  idPlaylist?: string;
}

const ScreenPlaylist = ({ form, idPlaylist }: ScreenPlaylistProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const contentModalRef = useContentModalRef();
  const { stores } = useGetAllStores(true);
  const navigate = useNavigate();

  const contentItems: IContentItem[] = Form.useWatch('contentItems', form) || [];

  const { setNodeRef } = useDroppable({
    id: 'PLAYLIST_DROP_ZONE',
    data: { type: 'PLAYLIST' },
  });

  const totalDuration = useMemo(() => {
    return contentItems.reduce((sum, item) => sum + (item?.duration || 0), 0);
  }, [contentItems]);

  const storeOptions = useMemo(() => {
    return stores.map((store) => ({
      label: store.storeName,
      value: store.storeId,
    }));
  }, [stores]);

  const handleDeletePlaylist = useCallback(async (playlistId: string) => {
    try {
      setPageLoading(true);
      const res = await playlistApis.deletePlaylist(playlistId);

      if (res?.data?.data) {
        toast.success('Playlist deleted successfully');
        navigate('/playlist');
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  }, []);

  const onDeletePlaylist = useCallback((playlistId: string) => {
    modalConfirmRef.current?.open(() => handleDeletePlaylist(playlistId), {
      message: 'Are you sure you want to delete this playlist? This action cannot be undone.',
    });
  }, [handleDeletePlaylist]);

  const mediaItems = useMemo<IMediaItem[]>(() => {
    return contentItems.map((c) => ({
      id: c.id,
      name: c.name ?? '',
      url: c?.url ?? '',
      type: c.type,
      duration: c.duration,
      autoReplay: c.autoReplay ?? true,
      path: '',
      size: null,
    }));
  }, [contentItems]);

  const handleContentSelect = useCallback(
    async (content: IMediaItem[]) => {
      const result: IContentItem[] = await Promise.all(
        content.map(async (c, index) => {
          let duration = c.duration || 60;

          if (
            c.type === MediaType.VIDEO ||
            (c.type === MediaType.FILE && isMp4Url(c.url))
          ) {
            duration = await getVideoDuration(c.url);
          }

          return {
            url: c.url,
            type: c.type,
            duration,
            indexPlay: index,
            name: c.name,
            id: c.id,
            autoReplay: c.autoReplay ?? true,
          };
        }),
      );

      form?.setFieldsValue({ contentItems: result });
    },
    [form],
  );

  const handleZoneClick = useCallback(() => {
    requestAnimationFrame(() => {
      contentModalRef.current?.show(mediaItems, handleContentSelect);
    });
  }, [mediaItems, handleContentSelect]);

  const addContentItem = useCallback(
    (item: IMediaItem) => {
      const newItem: IContentItem = {
        url: item.url,
        type: item.type,
        duration: 60,
        indexPlay: contentItems.length,
        name: item.name,
        id: item.id,
        autoReplay: item.autoReplay ?? true,
      };

      form?.setFieldsValue({
        contentItems: [...contentItems, newItem],
      });
    },
    [contentItems, form],
  );

  const handlePreview = useCallback(() => {
    const previewKey = `preview-${Date.now()}`;
    sessionStorage.setItem(previewKey, JSON.stringify({ contentItems}));

    window.open(
      `/preview?key=${previewKey}`,
      '_blank',
    );
  }, [contentItems, idPlaylist]);

  return (
    <Container>
      <Row padding='16px' justifyContent='space-between' alignItems='center' gap={24}>
        <Form.Item name='name' noStyle rules={[{ required: true, message: 'Please enter playlist name' }]}>
          <BaseInput placeholder='Playlist Name' />
        </Form.Item>

        <Form.Item name='storeId' noStyle rules={[{ required: true }]}>
          <Select placeholder='Select Store' options={storeOptions} className='custom-select' />
        </Form.Item>

        <Row>
          {idPlaylist && (
            <Button
              type_delete
              prefix={<IcTrash width={20} height={20} fill={Colors.other[3]} />}
              onClick={() => onDeletePlaylist(idPlaylist)}
              lg
            >
              Delete
            </Button>
          )}

          <Button
            type_primary
            prefix={<IcShow width={20} height={20} fill={Colors.neutral.bg[1]} />}
            lg
            type='button'
            onClick={handlePreview}
          >
            Preview
          </Button>

          <Button
            type_second
            prefix={<IcCashierSave width={20} height={20} fill={Colors.neutral.bg[1]} />}
            lg
            type='submit'
          >
            {idPlaylist ? 'Update' : 'Save'}
          </Button>
        </Row>
      </Row>

      <Row width='100%' justifyContent='space-between' padding='0 16px 16px'>
        <Col gap={0}>
          <Text body_1 regular color='neutral.text.3' left>
            Total Duration:
          </Text>
          <Text title_h5 medium left>
            {dayjs().startOf('day').add(totalDuration, 'second').format('HH:mm:ss')}
          </Text>
        </Col>

        <Button
          type_new
          lg
          prefix={<IcPlus width={20} height={20} fill={Colors.primary[5]} />}
          onClick={handleZoneClick}
        >
          Add Content
        </Button>
      </Row>

      <Content ref={setNodeRef}>
        <DragDropPlaylist />
      </Content>

      <ModalConfirm ref={modalConfirmRef} />
      <ContentModal ref={contentModalRef} onAddLink={addContentItem} />
    </Container>
  );
};

export default ScreenPlaylist;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .custom-select {
    flex: 1;
    min-width: 160px;
  }
`;

const Content = styled.div`
  padding: 0 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
